import { doc, getDoc, addDoc, collection, FieldValue } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';

const FCM_API_URL = 'https://us-central1-bpnpshuttle.cloudfunctions.net/api';

interface FCMNotification {
  title: string;
  body: string;
}

interface FCMData {
  click_action?: string;
  screen?: string;
  inquiryId?: string;
  [key: string]: any;
}

interface SendNotificationParams {
  userId: string;
  notification: FCMNotification;
  data?: FCMData;
}

/**
 * FCM 푸시 알림 발송
 */
export async function sendFCMNotification({
  userId,
  notification,
  data = {},
}: SendNotificationParams): Promise<boolean> {
  try {
    // 사용자의 FCM 토큰 가져오기
    const authProfileDoc = await getDoc(doc(db, 'auth_profiles', userId));

    if (!authProfileDoc.exists()) {
      console.error('User auth profile not found:', userId);
      return false;
    }

    const fcmToken = authProfileDoc.data().fcmToken;

    if (!fcmToken) {
      console.warn('FCM token not found for user:', userId);
      return false;
    }

    // FCM 메시지 구성
    const message = {
      to: fcmToken,
      notification,
      data: {
        click_action: 'FLUTTER_NOTIFICATION_CLICK',
        ...data,
      },
    };

    // FCM API 호출
    const response = await fetch(FCM_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('FCM API error:', response.status, errorText);
      return false;
    }

    console.log('FCM notification sent successfully');
    return true;
  } catch (error) {
    console.error('Error sending FCM notification:', error);
    return false;
  }
}

/**
 * 민원 답변 알림 발송 및 저장
 */
export async function sendInquiryAnswerNotification(
  inquiryId: string,
  userId: string,
  inquiryTitle: string
): Promise<void> {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    // FCM 푸시 알림 발송
    const notificationSent = await sendFCMNotification({
      userId,
      notification: {
        title: '답변이 등록되었습니다!',
        body: `민원 제목: ${inquiryTitle}\n관리자가 답변을 등록했습니다.`,
      },
      data: {
        screen: 'inquiry_detail',
        inquiryId,
      },
    });

    // Firestore에 알림 내역 저장
    await addDoc(collection(db, 'notifications'), {
      userId,
      title: '답변이 등록되었습니다!',
      body: `민원 제목: ${inquiryTitle}\n관리자가 답변을 등록했습니다.`,
      type: 'inquiry_answer',
      referenceId: inquiryId,
      createdAt: new Date(),
      isRead: false,
      data: {
        screen: 'inquiry_detail',
        inquiryId,
      },
      fcmSent: notificationSent,
    });

    console.log('Inquiry answer notification saved to Firestore');
  } catch (error) {
    console.error('Error sending inquiry answer notification:', error);
    throw error;
  }
}
